import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { OsintResults, type OsintEntityView } from "@/components/osint/OsintResults";
import { EngineProgress } from "@/components/osint/EngineProgress";
import {
  startOsintSearch,
  drainOsintSearch,
  getOsintSearch,
  listOsintSearches,
  setEntityDecision,
  retryOsintJob,
  cancelOsintSearch,
  getEngineHealth,
} from "@/lib/osint/orchestrator.functions";
import { classifyInput } from "@/lib/osint/registry";
import { Loader2, Search, Square } from "lucide-react";


export const Route = createFileRoute("/osint")({
  head: () => ({
    meta: [
      { title: "OSINT Engines · CARIO" },
      {
        name: "description",
        content:
          "Run Sherlock, Maigret, Social Analyzer, theHarvester, Subfinder, SpiderFoot, Amass and BBOT from one search and see every result with its source chain.",
      },
      { property: "og:title", content: "OSINT Engines · CARIO" },
      {
        property: "og:description",
        content: "One universal search across eight open-source intelligence engines, with per-finding evidence and confidence.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OsintPage,
});

const PRESETS = [
  { value: "auto", label: "Auto (detect from input)" },
  { value: "person_osint", label: "Person OSINT" },
  { value: "username_osint", label: "Username OSINT" },
  { value: "company_intelligence", label: "Company intelligence" },
  { value: "domain_intelligence", label: "Domain intelligence" },
  { value: "infrastructure_intelligence", label: "Infrastructure intelligence" },
  { value: "email_investigation", label: "Email investigation" },
  { value: "web_presence", label: "Web presence" },
] as const;

function OsintPage() {
  const start = useServerFn(startOsintSearch);
  const drain = useServerFn(drainOsintSearch);
  const get = useServerFn(getOsintSearch);
  const list = useServerFn(listOsintSearches);
  const decide = useServerFn(setEntityDecision);
  const retry = useServerFn(retryOsintJob);
  const cancel = useServerFn(cancelOsintSearch);
  const health = useServerFn(getEngineHealth);
  const qc = useQueryClient();

  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"quick" | "standard" | "deep">("standard");
  const [preset, setPreset] = useState<(typeof PRESETS)[number]["value"]>("auto");
  const [searchId, setSearchId] = useState<string | null>(null);
  const [detected, setDetected] = useState<string>("");
  const draining = useRef<string | null>(null);

  useEffect(() => {
    setDetected(input.trim().length > 1 ? classifyInput(input) : "");
  }, [input]);

  const startM = useMutation({
    mutationFn: () => start({ data: { input: input.trim(), mode, preset } }),
    onSuccess: (res) => {
      setSearchId(res.searchId);
      qc.invalidateQueries({ queryKey: ["osint-searches"] });
      qc.invalidateQueries({ queryKey: ["osint-health"] });
    },
  });

  const historyQ = useQuery({ queryKey: ["osint-searches"], queryFn: () => list() });

  const resultQ = useQuery({
    queryKey: ["osint-search", searchId],
    queryFn: () => get({ data: { searchId: searchId! } }),
    enabled: !!searchId,
    refetchInterval: (q) => {
      const status = q.state.data?.search.status;
      return status === "running" ? 3000 : false;
    },
  });

  const healthQ = useQuery({ queryKey: ["osint-health"], queryFn: () => health(), staleTime: 60_000 });

  const data = resultQ.data;
  const entities = (data?.entities ?? []) as OsintEntityView[];
  const running = data?.search.status === "running";

  // Engines execute inside a request, so a still-running search needs the
  // client to keep asking the server for another bounded execution window.
  useEffect(() => {
    if (!searchId || !running || draining.current === searchId) return;
    draining.current = searchId;
    let cancelled = false;
    (async () => {
      for (let pass = 0; pass < 12 && !cancelled; pass++) {
        try {
          const res = await drain({ data: { searchId } });
          await resultQ.refetch();
          if (res.status !== "running" || res.remaining === 0) break;
        } catch {
          break;
        }
      }
      qc.invalidateQueries({ queryKey: ["osint-health"] });
      if (draining.current === searchId) draining.current = null;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId, running]);

  const entityById = new Map(entities.map((e) => [e.id, e]));

  const links = (data?.relationships ?? [])
    .filter((r) => entityById.has(r.from) && entityById.has(r.to))
    .slice(0, 200);

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="OSINT Engines"
        title="Universal search"
        description="One input, routed to the right open-source engines. Every finding keeps the engine, source and timestamp that produced it."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (input.trim().length > 1) startM.mutate();
          }}
          className="glass space-y-3 rounded-xl p-4"
        >
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Name, username, email, phone, company, domain, URL or IP"
              className="flex-1 rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40"
            />
            <button
              type="submit"
              disabled={startM.isPending || input.trim().length < 2}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {startM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Run search
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as typeof preset)}
              className="rounded-md border border-panel-border bg-background/40 px-2 py-1.5"
            >
              {PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <div className="flex rounded-md border border-panel-border">
              {(["quick", "standard", "deep"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 capitalize ${mode === m ? "bg-cyan/15 text-cyan" : "text-muted-foreground"}`}
                >
                  {m}
                </button>
              ))}
            </div>
            {detected && (
              <span className="rounded border border-panel-border px-2 py-1 font-mono text-[10px] uppercase text-muted-foreground">
                detected: {detected}
              </span>
            )}
            {detected === "email" && input.includes("@") && (
              <Link
                to="/email"
                search={{ q: input.trim() }}
                className="inline-flex items-center gap-1 rounded border border-cyan/40 bg-cyan/10 px-2 py-1 text-cyan"
              >
                Run deep email intelligence
              </Link>
            )}
            {running && (
              <button
                type="button"
                onClick={() => searchId && cancel({ data: { searchId } }).then(() => resultQ.refetch())}
                className="inline-flex items-center gap-1 rounded border border-danger/40 bg-danger/10 px-2 py-1 text-danger"
              >
                <Square className="h-3 w-3" /> Stop
              </button>
            )}
          </div>
          {startM.error && <div className="text-xs text-danger">{(startM.error as Error).message}</div>}
          {startM.data?.skipped?.length ? (
            <div className="text-[11px] text-muted-foreground">
              Not run: {startM.data.skipped.map((s) => `${s.engine} (${s.reason})`).join(" · ")}
            </div>
          ) : null}

        </form>

        {data && (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <StatTile label="Status" value={String(data.search.status)} tone="cyan" />
              <StatTile label="Findings" value={entities.length} tone="success" />
              <StatTile label="Engines" value={new Set(data.jobs.map((j) => j.engine)).size} tone="cyan" />
              <StatTile
                label="Requests"
                value={data.jobs.reduce((s, j) => s + j.requestCount, 0)}
                tone="amber"
              />
            </div>

            <EngineProgress
              jobs={data.jobs}
              onRetry={(jobId) => retry({ data: { jobId } }).then(() => resultQ.refetch())}
            />

            <OsintResults
              entities={entities}
              onDecision={(entityId, decision) =>
                decide({ data: { entityId, decision } }).then(() => resultQ.refetch())
              }
            />

            {links.length > 0 && (
              <section className="glass rounded-xl p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Identifier graph</div>
                <ul className="mt-3 space-y-1 text-xs">
                  {links.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 rounded border border-panel-border bg-background/40 p-2">
                      <span className="break-all font-medium">{entityById.get(r.from)?.value}</span>
                      <span className="font-mono text-[10px] uppercase text-cyan">{r.kind.replace(/_/g, " ")}</span>
                      <span className="break-all font-medium">{entityById.get(r.to)?.value}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground">{Math.round(r.confidence * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <section className="glass rounded-xl p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Engine coverage</div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="font-mono text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Engine</th>
                  <th>Group</th>
                  <th>Accepts</th>
                  <th>Runs (7d)</th>
                  <th>Success</th>
                  <th>Findings</th>
                  <th>Avg time</th>
                </tr>
              </thead>
              <tbody>
                {(healthQ.data?.engines ?? []).map((e) => (
                  <tr key={e.key} className="border-t border-panel-border">
                    <td className="py-1.5 font-medium">{e.label}</td>
                    <td className="text-muted-foreground">{e.worker}</td>
                    <td className="text-muted-foreground">{e.inputs.join(", ")}</td>
                    <td>{e.runs}</td>
                    <td>{e.successRate == null ? "—" : `${e.successRate}%`}</td>
                    <td>{e.results}</td>
                    <td>{e.avgRuntimeMs == null ? "—" : `${(e.avgRuntimeMs / 1000).toFixed(1)}s`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="glass rounded-xl p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Source registry</div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Every source runs from CARIO's own backend against public endpoints. No external worker, no container to
            operate.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="font-mono text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Source</th>
                  <th>Category</th>
                  <th>API key</th>
                  <th>Health</th>
                  <th>Latency</th>
                </tr>
              </thead>
              <tbody>
                {(healthQ.data?.sources ?? []).map((s) => (
                  <tr key={s.id} className="border-t border-panel-border">
                    <td className="py-1.5">
                      <a href={s.docs} target="_blank" rel="noreferrer" className="font-medium hover:text-cyan">
                        {s.name}
                      </a>
                      <div className="text-[10px] text-muted-foreground">{s.supports.join(", ")}</div>
                    </td>
                    <td className="text-muted-foreground">{s.category.replace(/_/g, " ")}</td>
                    <td className="text-muted-foreground">{s.requiresApiKey ? "required" : "none"}</td>
                    <td>
                      <span
                        className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${
                          s.status === "online"
                            ? "border-success/40 bg-success/10 text-success"
                            : s.status === "degraded"
                              ? "border-amber/40 bg-amber/10 text-amber"
                              : "border-danger/40 bg-danger/10 text-danger"
                        }`}
                        title={s.error ?? ""}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td className="text-muted-foreground">{s.latencyMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>


        <section className="glass rounded-xl p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Recent searches</div>
          <ul className="mt-3 space-y-1 text-xs">
            {(historyQ.data ?? []).map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setSearchId(s.id)}
                  className={`flex w-full items-center gap-2 rounded border border-panel-border bg-background/40 p-2 text-left hover:bg-panel/50 ${
                    searchId === s.id ? "border-cyan/40" : ""
                  }`}
                >
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">{s.entityType}</span>
                  <span className="break-all font-medium">{s.input}</span>
                  <span className="ml-auto text-muted-foreground">
                    {s.status} · {new Date(s.createdAt).toLocaleString()}
                  </span>
                </button>
              </li>
            ))}
            {!historyQ.data?.length && <li className="text-muted-foreground">No searches yet.</li>}
          </ul>
        </section>
      </ModuleShell>
    </AppLayout>
  );
}
